/**
 * Toolbar-level change-type filter for the Products grid.
 *
 * Filters server-side via the /search/products `changeType` param so that
 * footer aggregations and totalCount reflect the selected change type.
 *
 * Pairs with SelectMonthRangeFilter — together they let leadership pull
 * per-month, per-classification totals without 4 separate columns.
 */
import AutorenewIcon from '@mui/icons-material/Autorenew'
import DomainAddIcon from '@mui/icons-material/DomainAdd'
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline'
import TrendingDownIcon from '@mui/icons-material/TrendingDown'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import { Box, FormControl, InputLabel, MenuItem, Select, SelectChangeEvent } from '@mui/material'

export type ChangeTypeValue = '' | 'New Business' | 'Upsell' | 'Downsell' | 'Renewal' | 'Removed'

interface SelectChangeTypeFilterProps {
  value: ChangeTypeValue
  onChange: (value: ChangeTypeValue) => void
}

const OPTIONS: { value: Exclude<ChangeTypeValue, ''>; label: string; Icon: typeof DomainAddIcon; color: string }[] = [
  { value: 'New Business', label: 'New Business', Icon: DomainAddIcon, color: 'primary.main' },
  { value: 'Upsell', label: 'Upsell', Icon: TrendingUpIcon, color: 'success.main' },
  { value: 'Downsell', label: 'Downsell', Icon: TrendingDownIcon, color: 'warning.main' },
  { value: 'Renewal', label: 'Renewal', Icon: AutorenewIcon, color: 'text.secondary' },
  { value: 'Removed', label: 'Removed', Icon: RemoveCircleOutlineIcon, color: 'error.main' },
]

const SelectChangeTypeFilter = ({ value, onChange }: SelectChangeTypeFilterProps) => {
  const handleChange = (event: SelectChangeEvent<string>): void => {
    onChange(event.target.value as ChangeTypeValue)
  }

  // displayEmpty + a renderValue that returns null for the empty case lets the
  // label sit in its resting (centered-inside-field) position when no filter
  // is active — otherwise returning ANY JSX from renderValue forces MUI to
  // treat the field as having a value and the label always floats, which gets
  // clipped by the Toolbar's tight vertical bounds.
  return (
    <FormControl size="small" sx={{ minWidth: 160 }}>
      <InputLabel id="change-type-select-label" shrink={Boolean(value)}>
        Change Type
      </InputLabel>
      <Select
        labelId="change-type-select-label"
        value={value}
        onChange={handleChange}
        label="Change Type"
        displayEmpty
        notched={Boolean(value)}
        renderValue={selected => {
          if (!selected) return null
          const opt = OPTIONS.find(o => o.value === selected)
          if (!opt) return selected
          const Icon = opt.Icon
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Icon sx={{ color: opt.color, fontSize: 18 }} />
              {opt.label}
            </Box>
          )
        }}
      >
        <MenuItem value="">
          <em>All Types</em>
        </MenuItem>
        {OPTIONS.map(opt => {
          const Icon = opt.Icon
          return (
            <MenuItem key={opt.value} value={opt.value}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Icon sx={{ color: opt.color, fontSize: 18 }} />
                {opt.label}
              </Box>
            </MenuItem>
          )
        })}
      </Select>
    </FormControl>
  )
}

export default SelectChangeTypeFilter
